import { useRecoilState } from "recoil";
import {
  hasUserBeenRedirectedToTheCustomerApplicationState,
  userAuthState,
  userCustomerApplicationState,
  userCustomerApplicationWizardState,
  userDataState,
  userFavoriteBondsState,
} from "../state";
import { API } from "aws-amplify";
import { doCustomGetUserData } from "./backendApi/amplifyGraphQl/CustomGetUserData/doCustomGetUserData";
import { useCallback } from "react";
import { route } from "../routes";
import { fetchCustomerApplication, fetchCustomerApplicationWizard } from "@goodfill/backend-api-client";
import { backendFetcher } from "./backendApi/backendApi";
import { toastErrorHandler } from "../unexpectedErrorHandler";
import { useNavigate } from "react-router-dom";
import { CustomerApplicationWizardSave } from "@goodfill/domain-customer-application-types";
import { useLogout } from "./useLogout";

export function useRefreshUserDataHook() {
  const [userAuthData, setUserAuthData] = useRecoilState(userAuthState);
  const [, setUserDataState] = useRecoilState(userDataState);
  const [, setUserFavoriteBondsState] = useRecoilState(userFavoriteBondsState);
  const [, setUserCustomerApplicationState] = useRecoilState(userCustomerApplicationState);
  const [, setUserCustomerApplicationWizardState] = useRecoilState(userCustomerApplicationWizardState);
  const [hasUserBeenRedirectedToTheCustomerApplication, setHasUserBeenRedirectedToTheCustomerApplication] =
    useRecoilState(hasUserBeenRedirectedToTheCustomerApplicationState);

  const navigate = useNavigate();
  const logout = useLogout();

  // rome-ignore lint/nursery/useExhaustiveDependencies: no navigate, causes infinite redirects
  return useCallback(async () => {
    if (userAuthData === null) {
      navigate(route.signIn());
      return;
    }

    try {
      console.log("Refreshing user data...");
      const data = await doCustomGetUserData(API, userAuthData.user.userId);
      setUserDataState(data);
      setUserFavoriteBondsState(data?.favoriteBonds ?? []);

      (
        await fetchCustomerApplication(backendFetcher, userAuthData.idToken, {
          userId: userAuthData.user.userId,
        })
      ).match({
        success: (data) => {
          setUserCustomerApplicationState(data);
        },
        businessMatcher: {
          CustomerApplicationSubmissionNotFound: () => {
            if (!hasUserBeenRedirectedToTheCustomerApplication) {
              setHasUserBeenRedirectedToTheCustomerApplication(true);
              navigate(route.customerApplication(1));
            }
          },
        },
        unexpected: toastErrorHandler,
        unauthorized: logout,
      });

      (
        await fetchCustomerApplicationWizard(backendFetcher, userAuthData.idToken, {
          userId: userAuthData.user.userId,
        })
      ).match({
        success: (data: CustomerApplicationWizardSave) => {
          return setUserCustomerApplicationWizardState(data.wizardSubmission);
        },
        businessMatcher: {
          CustomerApplicationWizardSaveNotFound: () => {
            // intentionally no handling, if its missing we just don't set it and we leave null there
          },
        },
        unexpected: toastErrorHandler,
        unauthorized: logout,
      });
    } catch (e) {
      console.error(e, "Signing out, redirecting to sign-in...");
      logout();
    }
  }, [
    userAuthData,
    setUserAuthData,
    hasUserBeenRedirectedToTheCustomerApplication,
    setHasUserBeenRedirectedToTheCustomerApplication,
    // navigate, <--- Intentionally NOT, this will cause infinite loop of redirects
    setUserCustomerApplicationState,
    setUserDataState,
  ]);
}
